/**
 * TEMPORARY executory model — Renewal / Review obligation.
 *
 * Branches by kind:
 *   • renewal → record renewed-on / next-due / cost / ref + proof upload, with an
 *     optional approver (maker → checker). Submit flips to "Submit for Approval".
 *   • review  → a checklist of checkpoints (Confirm / Needs change). All confirmed
 *     → "Reviewed (Intact)". Any change → "Needs amendment" → roll out a meeting.
 *
 * Placeholder UI to be built upon — actions are stubbed (no persistence yet).
 * Opened from the R&R tab row, the Plan calendar drill-down, and the Inbox chain.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, ClipboardCheck, Upload, CalendarPlus } from "lucide-react";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import FormControl from "../../../components/common/FormControl";
import Badge from "../../../components/common/Badge";
import { statusVariant } from "../../../utils/statusVariant";
import { RenewalReviewItem } from "../../../services/renewalsReviewsService";

const REVIEW_CHECKLIST = [
    "Process steps still accurate",
    "Owners & responsibilities unchanged",
    "Controls still mapped & effective",
    "Regulatory / policy references current",
    "No environment / org change impacting it",
];

const APPROVERS = ["Finance Controller", "IT Head", "Plant Manager", "Compliance Lead"];

const sectStyle: React.CSSProperties = { fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: "#94a3b8", textTransform: "uppercase", margin: "18px 0 10px" };
const uploadStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, border: "1px dashed #c7d2e0", borderRadius: 9, padding: "10px 12px", color: "#64748b", fontSize: 13, background: "#fbfcfe" };

const RenewalReviewExecModal: React.FC<{
    item: RenewalReviewItem;
    onClose: () => void;
    readOnly?: boolean;
}> = ({ item, onClose, readOnly = false }) => {
    const navigate = useNavigate();
    const isReview = item.kind === "review";

    // renewal state
    const [approver, setApprover] = useState("");
    // review state
    const [checks, setChecks] = useState<("confirm" | "change")[]>(REVIEW_CHECKLIST.map(() => "confirm"));
    const needsAmendment = checks.some(c => c === "change");

    const setCheck = (i: number, v: "confirm" | "change") =>
        setChecks(prev => prev.map((c, idx) => (idx === i ? v : c)));

    return (
        <Modal isOpen onClose={onClose} size="md" closeOnOverlayClick={false}>
            <Modal.Header>
                <ClipboardCheck size={16} style={{ verticalAlign: -2, marginRight: 8 }} />
                {isReview ? "Review" : "Renewal"} — <span className="item-highlight">{item.title || "—"}</span>
            </Modal.Header>

            <Modal.Body>
                <div style={{ marginBottom: 12 }}>
                    <Badge variant="info" size="sm">{isReview ? "Review" : "Renewal"}</Badge>
                    {item.type_label && <Badge variant="neutral" size="sm" className="ml-1.5">{item.type_label}</Badge>}
                    <Badge variant={statusVariant(item.status)} size="sm" className="ml-1.5">{item.status || "Pending"}</Badge>
                </div>

                {/* ---------------- RENEWAL ---------------- */}
                {!isReview && (
                    <>
                        <div style={sectStyle}>What's being renewed</div>
                        <dl style={{ display: "grid", gridTemplateColumns: "140px 1fr", rowGap: 8, fontSize: 13 }}>
                            <dt className="text-muted">Item</dt><dd>{item.title}</dd>
                            <dt className="text-muted">Type</dt><dd>{item.type_label || item.type_code || "—"}</dd>
                            <dt className="text-muted">Due date</dt><dd>{item.scheduled_date || "—"}</dd>
                        </dl>

                        <div style={sectStyle}>Record the renewal</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <FormControl.DatePicker label="Renewed on" value="2026-06-05" onChange={() => {}} disabled={readOnly} />
                            <FormControl.DatePicker label="Next due" value="" onChange={() => {}} disabled={readOnly} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <FormControl.Input label="Cost (₹)" placeholder="e.g. 1,20,000" disabled={readOnly} />
                            <FormControl.Input label="Reference no." placeholder="invoice / certificate no." disabled={readOnly} />
                        </div>
                        {!readOnly && (
                            <div style={{ marginTop: 10 }}>
                                <label className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>Proof of renewal</label>
                                <div style={uploadStyle}><Upload size={14} /> Upload certificate / invoice / policy</div>
                            </div>
                        )}
                        <FormControl.Textarea label="Notes" rows={2} placeholder="Optional remarks…" disabled={readOnly} />

                        <div style={sectStyle}>Approval</div>
                        <FormControl.Select
                            label="Does this renewal need approval? (checker)"
                            value={approver}
                            onChange={(e: any) => setApprover(e.target.value)}
                            disabled={readOnly}
                        >
                            <option value="">No approval needed — renew directly</option>
                            {APPROVERS.map(a => <option key={a} value={a}>{a}</option>)}
                        </FormControl.Select>
                        {approver && (
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
                                Maker <b>you</b> → Checker <b>{approver}</b>. Stays <b>Pending approval</b> until the checker approves.
                            </div>
                        )}
                    </>
                )}

                {/* ---------------- REVIEW ---------------- */}
                {isReview && (
                    <>
                        <div style={sectStyle}>Process snapshot</div>
                        <dl style={{ display: "grid", gridTemplateColumns: "140px 1fr", rowGap: 8, fontSize: 13 }}>
                            <dt className="text-muted">Process</dt><dd>{item.title}</dd>
                            <dt className="text-muted">Review due</dt><dd>{item.scheduled_date || "—"}</dd>
                        </dl>

                        <div style={sectStyle}>Review checklist</div>
                        {REVIEW_CHECKLIST.map((c, i) => (
                            <div key={i} className={`ck${checks[i] === "change" ? " chg" : ""}`}
                                 style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #e6eaf0", borderRadius: 10, padding: "8px 12px", fontSize: 13.5, marginTop: 6, ...(checks[i] === "change" ? { borderColor: "#fca5a5", background: "#fef2f2" } : {}) }}>
                                <span style={{ flex: 1 }}>{c}</span>
                                <div className="seg sm" style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 8, padding: 3, gap: 2 }}>
                                    {(["confirm", "change"] as const).map(v => (
                                        <button key={v} type="button"
                                            disabled={readOnly}
                                            onClick={() => { if (!readOnly) setCheck(i, v); }}
                                            style={{
                                                border: 0, padding: "4px 10px", borderRadius: 6, fontSize: 11.5, fontWeight: 600, cursor: readOnly ? "default" : "pointer",
                                                background: checks[i] === v ? "#fff" : "transparent",
                                                color: checks[i] === v ? (v === "change" ? "#dc2626" : "#1d4ed8") : "#64748b",
                                            }}>
                                            {v === "confirm" ? "Confirm" : "Change"}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <FormControl.Textarea label="Remarks" rows={2} placeholder="Observations from the review…" disabled={readOnly} />

                        <div style={sectStyle}>Confirmation</div>
                        <div style={{
                            padding: "10px 12px", borderRadius: 10, fontSize: 13.5, fontWeight: 600,
                            border: `1px solid ${needsAmendment ? "#fca5a5" : "#1d4ed8"}`,
                            background: needsAmendment ? "#fef2f2" : "#eff4ff",
                            color: needsAmendment ? "#991b1b" : "#1e40af",
                        }}>
                            {needsAmendment
                                ? "⚠ Needs amendment — roll out a meeting to amend the process"
                                : "✓ All confirmed — review the process as-is (Intact)"}
                        </div>
                        {needsAmendment && (
                            <div style={{ marginTop: 10 }}>
                                <label className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>Amendment meeting — invite</label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                                    {["Process Owner", "Compliance Lead"].map(p => (
                                        <Badge key={p} variant="info" size="sm">{p}</Badge>
                                    ))}
                                    <Badge variant="neutral" size="sm">+ add people</Badge>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div style={{ marginTop: 16, padding: 12, background: "#f8fafc", borderRadius: 8, fontSize: 12, color: "#64748b" }}>
                    Temporary model. {isReview
                        ? "Real review (checklist persistence, meeting rollout, advance next review cycle)"
                        : "Real renewal (record + proof + maker-checker approval, advance next due)"} wires in a later phase.
                </div>
            </Modal.Body>

            <Modal.Footer>
                {item.route_path && (
                    <Button variant="outline" icon={ExternalLink} onClick={() => navigate(item.route_path!)}>
                        View record
                    </Button>
                )}
                <div style={{ flex: 1 }} />
                {!readOnly && (isReview ? (
                    needsAmendment ? (
                        <Button variant="update" icon={CalendarPlus} disabled title="Meeting rollout wires later">
                            Roll out amendment meeting
                        </Button>
                    ) : (
                        <Button variant="success" icon={ClipboardCheck} disabled title="Wires later">
                            Mark Reviewed (Intact)
                        </Button>
                    )
                ) : approver ? (
                    <Button variant="fill" disabled title="Approval workflow wires later">
                        Submit for Approval
                    </Button>
                ) : (
                    <Button variant="success" icon={ClipboardCheck} disabled title="Wires later">
                        Mark Renewed
                    </Button>
                ))}
                <Button variant="outline" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RenewalReviewExecModal;
